import type { Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import type { ObservationTemplate } from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  componentPattern,
  formatCodes,
  parseComponentText,
  withComponentValue,
} from '@seen/core/models/observationComponents'
import { withDerivedComponents } from '@seen/core/observation-logic/derivations/components'
import { type JSX, useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  CHOOSE_PARTICIPANT,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationVitalInput } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalInput.tsx'
import { ObservationVitalNote } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalNote.tsx'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

function htmlToText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// A code recorded as components (blood pressure, for one), typed as one line
// the template's format describes. The line fills the required parts; the
// optional ones are set in the details. The typed line stays the record's
// first note, as the vitals keep it.
export function ObservationNodeComponents({
  template,
  attributes,
  updateAttributes,
  participant,
  noParticipant = false,
  editable = true,
  recordStatus,
}: {
  template: ObservationTemplate
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  // No participant chosen: nothing can be recorded.
  noParticipant?: boolean
  // Called by the node's record once a first answer is on the node.
  onReferencePersisted?: () => void
  // Whether the note can be changed, and so the vital cleared.
  editable?: boolean
  recordStatus?: ObservationCaptureTarget['recordStatus']
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const record = useNodeRecord()
  const { observation } = record
  // Recorded for someone else than the editor's participant: shown, and
  // clearable, but not changed from here.
  const foreign = isForeignRecord(
    observation,
    noParticipant ? null : participant
  )
  // A note that cannot be changed (signed, posted, read-only) takes no
  // answer here, as every observation control.
  const locked = !editable || isReadOnly || noParticipant || foreign

  // A note written before the parts are recorded, for its record.
  const pendingNote = useRef('')
  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const practitioner = useCurrentPractitioner()
  const derivedObservations = useFeatureFlag(featureFlags.structuredCapture)

  const label = ExtendedObservation.getNickname(
    attributes.code,
    attributes.codingDisplay
  )

  const additionalExtensions = useMemo(() => {
    const regex = componentPattern(template)
    return regex
      ? [
          FirstMatchDecorator.configure({
            regex,
            className: 'font-mono tabular-nums text-foreground',
          }),
        ]
      : []
  }, [template])

  const updateComponents = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const values = parseComponentText(template, htmlToText(html))
      try {
        await record.patch((o) => {
          // Only the parts the line names; optional ones set in the
          // details are kept.
          let components = o.component
          for (const code of formatCodes(template)) {
            const spec = template.components.find(
              (candidate) => candidate.coding.code === code
            )
            if (spec) {
              components = withComponentValue(
                template,
                components,
                spec,
                values?.[code]
              )
            }
          }
          if (derivedObservations) {
            components = withDerivedComponents(template, components)
          }
          o.component = components?.length ? components : undefined
          o.setNoteFirst(html, practitioner.reference)
        }, observation)
      } catch (error) {
        toast.error(
          error instanceof Error
            ? `Failed to update ${label}: ${error.message}`
            : `Failed to update ${label}`
        )
        throw error
      }
    },
    [record, practitioner.reference, template, label, derivedObservations]
  )

  const debouncedOnChange = useDebouncedCallback(async (html: string) => {
    if (attributes.reference || attributes.draft) {
      invariant(observation, 'observation is required')
      await updateComponents(observation, html)
      return
    }
    if (createObservationPromiseRef.current) {
      // Wait for the record being created, then update it.
      const createdObservation = await createObservationPromiseRef.current
      await updateComponents(createdObservation, html)
      return
    }
    const values = parseComponentText(template, htmlToText(html))
    if (!values) {
      toast.error(`Invalid ${label} values.`)
      return
    }
    const created = ExtendedObservation.fromComponents({
      template,
      values,
      code: codeableConcept({
        code: attributes.code,
        system: attributes.system,
        display: attributes.codingDisplay,
      }),
      participant,
      practitioner: practitioner.reference,
      note: html,
      status: recordStatus,
      clinicianNote: pendingNote.current,
    })
    // Parts computed from the others (mean arterial pressure) are filled in
    // with them.
    if (derivedObservations) {
      created.component = withDerivedComponents(template, created.component)
    }
    createObservationPromiseRef.current = record.create(created)
    try {
      await createObservationPromiseRef.current
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Failed to submit ${label}: ${error.message}`
          : `Failed to submit ${label}`
      )
      throw error
    } finally {
      createObservationPromiseRef.current = null
    }
  }, 500) // Short, so a note submitted right after typing has its reference; also flushed on blur.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={label}
      className="relative"
      noHover
      updateAttributes={editable ? updateAttributes : undefined}
      participant={participant}
      foreign={foreign}
      blockedReason={
        noParticipant && !observation ? CHOOSE_PARTICIPANT : undefined
      }
    >
      <ObservationVitalInput
        code={attributes.code}
        value={observation?.noteFirstText ?? ''}
        debouncedOnChange={debouncedOnChange}
        additionalExtensions={additionalExtensions}
        autofocus={attributes.autofocus}
        onAutofocused={handleAutofocused}
        disabled={locked}
      />
      <ObservationVitalNote
        observation={observation}
        onPendingChange={(note) => {
          pendingNote.current = note
        }}
        disabled={locked}
      />
    </ObservationNodeFrame>
  )
}
